import React, { useState, useEffect, useCallback } from 'react';
import CtxLine from './StockCtx';
import MarketStrip from './MarketStrip';
import PauseButton from './PauseButton';
import { charges } from './charges';

const C = { grn:'#1D9E75', red:'#E24B4A', amber:'#BA7517', blue:'#378ADD',
            purple:'#534AB7', t2:'var(--t2)' };
const money = v => (v<0?'-':'') + '\u20B9' + Math.abs(Math.round(v)).toLocaleString('en-IN');
const pct = v => (v>0?'+':'') + (v||0).toFixed(2) + '%';

// Opstra colours the outlook cell itself, which is what makes the screen
// readable at a glance — a green badge in a column of red ones is the signal.
const BADGE = {
  'Significant Long Buildup':  ['#0F6E56', '#fff'],
  'Long Buildup':              ['#1D9E75', '#fff'],
  'Significant Short Buildup': ['#A32D2D', '#fff'],
  'Short Buildup':             ['#E24B4A', '#fff'],
  'Long Unwinding':            ['#EF9F27', '#412402'],
  'Significant Long Unwinding':['#BA7517', '#fff'],
  'Short Covering':            ['#FAC775', '#412402'],
  'Significant Short Covering':['#EF9F27', '#412402'],
};
const Badge = ({label}) => {
  if (!label) return <span style={{color:'var(--t2)'}}>&mdash;</span>;
  const [bg, fg] = BADGE[label] || ['#B4B2A9', '#2C2C2A'];
  return <span style={{ background:bg, color:fg, fontSize:11, fontWeight:600,
                        padding:'3px 10px', borderRadius:20, whiteSpace:'nowrap',
                        display:'inline-block' }}>{label}</span>;
};

const Card = ({children, style}) => (
  <div style={{ background:'var(--bg1)', border:'1px solid var(--bd)', borderRadius:10,
                padding:16, marginBottom:14, ...style }}>{children}</div>);

export default function IntraDayOI() {
  const [ov, setOv] = useState(null);
  const [sl, setSl] = useState(null);
  const [sig, setSig] = useState(null);
  const [pap, setPap] = useState(null);
  const [view, setView] = useState('signals');
  const [showRules, setShowRules] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filt, setFilt] = useState('qualifying');
  const [lastAt, setLastAt] = useState(null);

  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    const g = async p => {
      try {
        const r = await fetch(p);
        if (!r.ok) throw new Error(p + ' -> ' + r.status);
        return await r.json();
      } catch(e) { setErr(String(e.message || e)); return null; }
    };
    setErr(null);
    const [o, s2, g2, p2] = await Promise.all([
      g('/intraday/overview'), g('/intraday/shortlist?only_tradeable=false'),
      g('/intraday/signals'), g('/intraday/paper')]);
    if (o) setOv(o);
    setSl(s2); setSig(g2); setPap(p2);
    setLastAt(new Date());
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const take = async (id) => {
    setBusy(id);
    let notional = 100000;
    try { const a = await (await fetch('/intraday/align?signal_id=' + id)).json();
      if (a.reason) { const d = a.detail || {}; const f = v => v == null ? '-' : (v > 0 ? '+' : '') + v + '%';
        if (!window.confirm(`Warning: ${a.reason}.\nMarket ${f(d.mkt)} vs yesterday · ${d.sector || 'no sector'} ${f(d.sector_open)} since open · ${d.industry || 'no industry'} ${f(d.industry_pc)} · day: ${d.seg_day || '–'}\n\nTake anyway at 60% size with a 0.6% stop?`)) { setBusy(null); return; }
        notional = 60000; } } catch (e) {}
    await fetch('/intraday/paper/take', { method:'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ signal_id:id, notional }) });
    await load(); setBusy(null);
  };
  const takeRev = async (r) => {   // 4 Nov: Reverse = one order on the opposite side
    const opp = r.direction === 'LONG' ? 'SHORT' : 'LONG';
    let a = {}; try { a = await (await fetch('/intraday/align?signal_id=' + r.id + '&reverse=1')).json(); } catch (e) {}
    const d = a.detail || {}, f = v => v == null ? '-' : (v > 0 ? '+' : '') + v + '%';
    if (!window.confirm(a.reason ? `Warning: the reverse (${opp}) is ${a.reason}.\nMarket ${f(d.mkt)} vs yesterday · ${d.sector || 'no sector'} ${f(d.sector_open)} since open · ${d.industry || 'no industry'} ${f(d.industry_pc)} · day: ${d.seg_day || '–'}\n\nReverse anyway at 60% size with a 0.6% stop?`
      : `Reverse: take ${opp} ${r.symbol}? (signal was ${r.direction})\nThe reverse is in line with market, sector and industry: full size.`)) return;
    setBusy(r.id);
    try { const j = await (await fetch('/intraday/paper/take', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ signal_id: r.id, notional: 100000, reverse: true }) })).json(); if (!j.ok) alert('Reverse failed: ' + (j.error || '')); } catch (e) { alert('Reverse failed: ' + e); }
    await load(); setBusy(null);
  };
  const exitPos = async (id, partial) => {
    setBusy(id);
    try { const r = await fetch('/intraday/paper/exit', { method:'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ position_id:id, partial, reason: partial?'partial':'manual' }) });
      const j = await r.json(); if (!j.ok) alert('Exit failed: ' + (j.error || r.status)); } catch (e) { alert('Exit failed: ' + e); }
    await load(); setBusy(null);
  };

  if (!ov) return (
    <div style={{padding:20, color: err ? C.red : C.t2, fontSize:13}}>
      {err ? 'could not load: ' + err : 'loading\u2026'}
    </div>);
  const open = pap?.open || [];
  const closed = pap?.closed || [];
  const openPnl = open.reduce((a,x) => a + (x.pnl||0), 0);
  const NETO = x => (Number(x.pnl) || 0) - charges(x.direction, x.qty, x.entry, x.exit ?? x.ltp);

  return (
    <div style={{ padding:'0 4px' }}>
      <MarketStrip />
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
        <h2 style={{ margin:0, fontSize:20 }}>IntraDay · strategy 1</h2>
        <PauseButton name="intraday_oi" label="IntraDay OI trades (the watcher)" />
        <span style={{ fontSize:11, padding:'3px 9px', borderRadius:5, fontWeight:600,
                       background:C.amber+'1a', color:C.amber }}>PAPER ONLY</span>
        <span style={{ fontSize:12.5, color:C.t2 }}>{ov.strategy?.name}</span>
        {lastAt && <span style={{ fontSize:11.5, color:C.t2 }}>
          updated {lastAt.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',
                                                      second:'2-digit',hour12:false})}
        </span>}
        <button onClick={() => setShowRules(!showRules)}
          style={{ marginLeft:'auto', fontSize:12, padding:'4px 12px', borderRadius:6,
                   border:'1px solid var(--bd)', background:'var(--bg1)',
                   color:C.t2, cursor:'pointer' }}>
          {showRules ? 'hide' : 'what this is, and what it tested at'}
        </button>
      </div>

      {showRules && (
        <Card style={{ borderLeft:`3px solid ${C.amber}`, borderRadius:0 }}>
          <div style={{ fontSize:12.5, lineHeight:1.65 }}>
            <b>The rules</b><br/>{ov.strategy?.rules}
            <div style={{ marginTop:12 }}>
              <b style={{color:C.red}}>What it tested at</b><br/>
              {ov.strategy?.backtest}
            </div>
            <div style={{ marginTop:12, color:C.t2 }}>
              Source: {ov.strategy?.source}. The screen exists to find out whether
              choosing <i>which</i> signals to take beats the mechanical version.
              Nothing here places a real order.
            </div>
          </div>
        </Card>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',
                    gap:10, marginBottom:14 }}>
        {[['screened', ov.screened], ['on the list', `${ov.n_long}L / ${ov.n_short}S`],
          ['signals today', ov.signals_today], ['open', open.length],
          ['today unrealised P&L', money(open.reduce((a, x) => a + NETO(x), 0)), money(openPnl)], ['today closed P&L', money(closed.filter(x => x.for_date === new Date().toLocaleDateString('en-CA', { timeZone:'Asia/Kolkata' })).reduce((a, x) => a + NETO(x), 0)), money(closed.filter(x => x.for_date === new Date().toLocaleDateString('en-CA', { timeZone:'Asia/Kolkata' })).reduce((a, x) => a + (Number(x.pnl) || 0), 0))],
          ['total realised till date P&L', money((ov.realised || 0) - closed.reduce((a, x) => a + charges(x.direction, x.qty, x.entry, x.exit), 0)), money(ov.realised)],
          ['win rate', ov.win_rate != null ? ov.win_rate + '%' : '\u2014']].map(([k,v,g],i) => (
          <Card key={i} style={{ marginBottom:0, padding:'10px 12px' }}>
            <div style={{ fontSize:10.5, color:C.t2, textTransform:'uppercase',
                          letterSpacing:0.4 }}>{k}</div>
            <div style={{ fontSize:18, fontWeight:600, marginTop:3,
                          color: k.includes('P&L')||k==='realised'
                                 ? ((typeof v==='string'&&v.startsWith('-'))?C.red:C.grn)
                                 : 'inherit' }}>{v}</div>{g != null && <div style={{ fontSize:11, color:C.t2, marginTop:2 }}>gross {g}</div>}
          </Card>))}
      </div>

      <div style={{ display:'flex', gap:6, marginBottom:12 }}>
        {[['signals', `Signals (${sig?.n||0})`], ['shortlist', `Shortlist (${sl?.n||0})`],
          ['positions', `Positions (${open.length})`], ['closed', `Closed (${closed.length})`]]
         .map(([k,label]) => (
          <button key={k} onClick={() => setView(k)}
            style={{ fontSize:12.5, padding:'6px 14px', borderRadius:7, cursor:'pointer',
                     border:`1px solid ${view===k?C.purple:'var(--bd)'}`,
                     background: view===k ? C.purple+'14' : 'var(--bg1)',
                     color: view===k ? C.purple : C.t2,
                     fontWeight: view===k ? 600 : 500 }}>{label}</button>))}
      </div>

      {view === 'signals' && (
        <Card>
          {!sig?.rows?.length ? (
            <div style={{ color:C.t2, fontSize:13, padding:'20px 0', textAlign:'center' }}>
              No signals yet today. The shortlist is checked every few minutes while
              the market is open; a signal appears when the EMAs line up in the
              shortlist's direction.
            </div>
          ) : sig.rows.map(r => (
            <div key={r.id} style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:14,
                 padding:'12px 0', borderBottom:'1px solid var(--bd)' }}><CtxLine view="oi" symbol={r.symbol} side={r.direction} />
              <div style={{ minWidth:52, fontSize:12, color:C.t2 }}>{r.ts}</div>
              <div style={{ minWidth:120 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{r.symbol}</div>
                <div style={{ fontSize:11.5, color: r.direction==='LONG'?C.grn:C.red,
                              fontWeight:600 }}>{r.direction}</div>
              </div>
              <div style={{ minWidth:170, display:'flex', flexDirection:'column', gap:4 }}>
                <Badge label={r.st} /><Badge label={r.mt} />
              </div>
              <div style={{ minWidth:110, fontSize:12 }}>
                at {r.price?.toFixed(2)}<br/>
                <span style={{ color:C.t2 }}>now {r.ltp?.toFixed(2) ?? '\u2014'}</span>
              </div>
              <div style={{ minWidth:70, fontSize:13, fontWeight:600,
                            color: (r.since||0) >= 0 ? C.grn : C.red }}>
                {r.since != null ? pct(r.since) : '\u2014'}
              </div>
              <div style={{ flex:1, fontSize:11.5, color:C.t2 }}>{r.note}</div>
              {r.taken ? (
                <span style={{ fontSize:11.5, color:C.t2 }}>taken</span>
              ) : (
                <button disabled={busy===r.id} onClick={() => take(r.id)}
                  style={{ fontSize:12, padding:'6px 16px', borderRadius:6,
                           border:`1px solid ${C.purple}`, background:C.purple+'12',
                           color:C.purple, fontWeight:600, cursor:'pointer' }}>
                  {busy===r.id ? '…' : 'Take \u20B91L'}
                </button>)}
              {!r.taken && <button disabled={busy===r.id} onClick={() => takeRev(r)} style={{ fontSize:12, padding:'6px 12px', marginLeft:6, borderRadius:6, border:'1px solid #b36b00', background:'#b36b0012', color:'#b36b00', fontWeight:600, cursor:'pointer' }}>Reverse</button>}
            </div>))}
        </Card>)}

      {view === 'shortlist' && (
        <Card>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12,
                        flexWrap:'wrap' }}>
            <span style={{ fontSize:12, color:C.t2, marginRight:4 }}>
              From {sl?.for_date} ·</span>
            {[['qualifying','Qualifying only'], ['all','All ' + (sl?.rows?.length||0)],
              ['Significant Long Buildup','Sig. long buildup'],
              ['Significant Short Buildup','Sig. short buildup'],
              ['Short Covering','Short covering'],
              ['Long Unwinding','Long unwinding']].map(([k,lbl]) => (
              <button key={k} onClick={() => setFilt(k)}
                style={{ fontSize:11.5, padding:'4px 11px', borderRadius:20,
                         cursor:'pointer',
                         border:`1px solid ${filt===k?C.purple:'var(--bd)'}`,
                         background: filt===k ? C.purple+'14' : 'transparent',
                         color: filt===k ? C.purple : C.t2,
                         fontWeight: filt===k ? 600 : 500 }}>{lbl}</button>))}
          </div>
          <div style={{ fontSize:11.5, color:C.t2, marginBottom:10 }}>
            A row qualifies when both outlooks agree on direction and at least one
            shows an OI change above 4%. That threshold is inferred from two worked
            examples, not published \u2014 the other filters show the raw screen.
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
            <thead><tr style={{ fontSize:10.5, color:C.t2, textTransform:'uppercase' }}>
              {['stock','dir','fut close','equity now','1D price','1D OI','5D price','5D OI',
                'short term','medium term'].map(h =>
                <th key={h} style={{ textAlign: h==='stock'||h.includes('term')?'left':'right',
                                     padding:'6px 8px', borderBottom:'1px solid var(--bd)',
                                     fontWeight:500 }}>{h}</th>)}
            </tr></thead>
            <tbody>{(sl?.rows||[]).filter(r =>
                filt==='all' ? true :
                filt==='qualifying' ? !!r.direction :
                (r.st===filt || r.mt===filt)
              ).map(r => (
              <tr key={r.symbol} style={{ borderBottom:'1px solid var(--bd)',
                   background: r.direction ? C.purple+'0a' : 'transparent' }}>
                <td style={{ padding:'8px', fontWeight:600 }}>{r.symbol}</td>
                <td style={{ padding:'8px', textAlign:'right', fontWeight:600,
                             color: r.direction==='LONG'?C.grn:(r.direction?C.red:C.t2) }}>
                  {r.direction || '\u2014'}</td>
                <td style={{ padding:'8px', textAlign:'right' }}>{r.px?.toFixed(1)}</td>
                <td style={{ padding:'8px', textAlign:'right' }}>
                  {r.ltp?.toFixed(1) ?? '--'}</td>
                <td style={{ padding:'8px', textAlign:'right',
                             color:(r.p1>=0?C.grn:C.red) }}>{pct(r.p1)}</td>
                <td style={{ padding:'8px', textAlign:'right',
                             color:(r.oi1>=0?C.blue:C.t2) }}>{pct(r.oi1)}</td>
                <td style={{ padding:'8px', textAlign:'right',
                             color:(r.p5>=0?C.grn:C.red) }}>{pct(r.p5)}</td>
                <td style={{ padding:'8px', textAlign:'right',
                             color:(r.oi5>=0?C.blue:C.t2) }}>{pct(r.oi5)}</td>
                <td style={{ padding:'8px' }}><Badge label={r.st} /></td>
                <td style={{ padding:'8px' }}><Badge label={r.mt} /></td>
              </tr>))}</tbody>
          </table>
        </Card>)}

      {view === 'positions' && (
        <Card>
          {!open.length ? (
            <div style={{ color:C.t2, fontSize:13, padding:'20px 0', textAlign:'center' }}>
              Nothing open.</div>
          ) : open.map(p => (
            <div key={p.id} style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:14,
                 padding:'12px 0', borderBottom:'1px solid var(--bd)' }}>
              <div style={{ minWidth:130 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{p.symbol}<RevTag id={p.id} /></div>
                <div style={{ fontSize:11.5, color: p.direction==='LONG'?C.grn:C.red,
                              fontWeight:600 }}>{p.direction} · {p.qty}</div>
              </div>
              <div style={{ minWidth:150, fontSize:12, color:C.t2 }}>
                in at {p.entry?.toFixed(2)} · {p.entry_ts}<br/>
                SL {p.stop?.toFixed(2)} · tgt {p.target?.toFixed(2)}
                {p.touched && <span style={{ marginLeft: 6, color: '#E24B4A', fontWeight: 600 }}>· {p.touched} (booked there at 15:25)</span>}
              </div>
              <div style={{ minWidth:90, fontSize:13 }}>{p.ltp?.toFixed(2)}</div>
              <div style={{ minWidth:110 }}>
                <div style={{ fontSize:15, fontWeight:600,
                              color:(p.pnl||0)>=0?C.grn:C.red }}>{money(p.pnl||0)}</div>
                <div style={{ fontSize:11.5, color:C.t2 }}>{pct(p.pct||0)}</div>
              </div>
              <div style={{ flex:1, fontSize:11.5, color:C.t2 }}>
                {p.partial ? `half booked at ${Number(p.partial).toFixed(2)}` : ''}
              </div>
              {!p.partial && (
                <button disabled={busy===p.id} onClick={() => exitPos(p.id, true)}
                  style={{ fontSize:12, padding:'6px 12px', borderRadius:6,
                           border:'1px solid var(--bd)', background:'var(--bg1)',
                           color:C.t2, cursor:'pointer' }}>Book half</button>)}
              <button disabled={busy===p.id} onClick={() => exitPos(p.id, false)}
                style={{ fontSize:12, padding:'6px 16px', borderRadius:6,
                         border:`1px solid ${C.red}`, background:C.red+'12',
                         color:C.red, fontWeight:600, cursor:'pointer' }}>
                {busy===p.id ? '…' : 'Exit'}</button>
              <div style={{ flexBasis:'100%', fontSize:12.5, overflowWrap:'anywhere', whiteSpace:'normal' }}><CtxLine view="oi" symbol={p.symbol} side={p.direction} /></div>
            </div>))}
        </Card>)}

      {view === 'closed' && (
        <Card>
          {!closed.length ? (
            <div style={{ color:C.t2, fontSize:13, padding:'20px 0', textAlign:'center' }}>
              Nothing closed yet.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead><tr style={{ fontSize:10.5, color:C.t2, textTransform:'uppercase' }}>
                {['date','stock','dir','qty','in','out','why','P&L'].map(h =>
                  <th key={h} style={{ textAlign: ['in','out','P&L','qty'].includes(h)?'right':'left',
                       padding:'6px 8px', borderBottom:'1px solid var(--bd)',
                       fontWeight:500 }}>{h}</th>)}
              </tr></thead>
              <tbody>{closed.map(p => (
                <tr key={p.id} style={{ borderBottom:'1px solid var(--bd)' }}>
                  <td style={{ padding:'8px' }}>{p.for_date}</td>
                  <td style={{ padding:'8px', fontWeight:600 }}>{p.symbol}</td>
                  <td style={{ padding:'8px', color:p.direction==='LONG'?C.grn:C.red,
                               fontWeight:600 }}>{p.direction}</td>
                  <td style={{ padding:'8px', textAlign:'right' }}>{p.qty}</td>
                  <td style={{ padding:'8px', textAlign:'right' }}>{p.entry?.toFixed(2)}</td>
                  <td style={{ padding:'8px', textAlign:'right' }}>{p.exit?.toFixed(2)}</td>
                  <td style={{ padding:'8px', fontSize:11.5, color:C.t2 }}>{p.exit_reason}</td>
                  <td style={{ padding:'8px', textAlign:'right', fontWeight:600,
                               color:(p.pnl||0)>=0?C.grn:C.red }}>{money(p.pnl||0)}</td>
                </tr>))}</tbody>
            </table>)}
        </Card>)}
    </div>);
}

// 4 Nov: REVERSE tag on reversed OI positions (shared poll every 10 s)
const RV = { ids: new Set(), subs: new Set(), timer: null };
const loadRV = async () => { try { const j = await (await fetch(`/intraday/reverses?t=${Date.now()}`, { cache: 'no-store' })).json(); RV.ids = new Set(j.ids || []); RV.subs.forEach(f => f(RV.ids)); } catch (e) {} };
function RevTag({ id }) { const [s, setS] = useState(RV.ids);
  useEffect(() => { RV.subs.add(setS); if (!RV.timer) { loadRV(); RV.timer = setInterval(loadRV, 10000); } return () => { RV.subs.delete(setS); if (!RV.subs.size) { clearInterval(RV.timer); RV.timer = null; } }; }, []);
  return s.has(id) ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#b36b0022', color: '#b36b00' }}>REVERSE</span> : null; }
